import { useEffect, useState, type ReactElement } from 'react'
import './useIsMobile.css'

/** Phone-only detection that re-runs on viewport resize. Initialised
 *  from window.matchMedia synchronously so first paint already picks
 *  the right layout. Mobile when EITHER dimension is phone-like:
 *  `(max-width: 720px)` catches phones in portrait;
 *  `(max-height: 500px)` catches phones in landscape. Tablets in
 *  either orientation stay above both thresholds and render desktop. */
export function useIsMobile(): boolean {
  const QUERY = '(max-width: 720px), (max-height: 500px)'
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(QUERY).matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(QUERY)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isMobile
}

/** Samsung Galaxy Z Fold family UA sniff — Fold 1 (SM-F900/F907),
 *  Z Fold 2 (SM-F916), Z Fold 3 (SM-F926), Z Fold 4 (SM-F936),
 *  Z Fold 5 (SM-F946), Z Fold 6 (SM-F956), and future SM-F9xx
 *  successors. Deliberately excludes Z Flip (SM-F7xx) — different
 *  form factor (clamshell, vertical hinge) with a tall narrow inner
 *  display that should NOT use the fold layout. The trailing model
 *  letter (e.g. SM-F936B for international, SM-F936U for US) is
 *  matched implicitly since the regex is unanchored on the right. */
function isGalaxyFoldUA(): boolean {
  if (typeof navigator === 'undefined') return false
  return /SM-F9\d{2}/.test(navigator.userAgent)
}

/** Galaxy Fold (and morphologically similar foldables) inner-display
 *  detection. The Fold's natural unfolded orientation is portrait
 *  with the hinge vertical; players rotate 90° to satisfy our
 *  landscape lock, which puts the hinge HORIZONTAL across the
 *  middle. Empirical geometry: ~828×690 CSS px, DPR ~2.625,
 *  `pointer: coarse`. The square-ish aspect (1:1 to 4:3) is the
 *  load-bearing discriminator — no laptop, phone, or standard
 *  tablet lands in this band. Mutually exclusive with
 *  `useIsMobile()` at these viewports: 828 > 720 and 690 > 500.
 *
 *  UA fallback: real-world Galaxy Z Fold 4 reports have it missing
 *  the matchMedia query in Samsung Internet — `pointer: coarse` can
 *  drop to `fine` when the S Pen / hover-capable input is present,
 *  and the device sits right at the edge of the width bound. So we
 *  OR the media query with a UA sniff for `SM-F9xx` plus a relaxed
 *  viewport range that still excludes the cover screen (~344×882
 *  CSS px portrait — handled by `useIsMobile()` via `max-width:
 *  720`) and any non-fold posture. */
export function useIsFoldOpen(): boolean {
  const QUERY =
    '(min-width: 721px) and (max-width: 900px)' +
    ' and (min-aspect-ratio: 1/1) and (max-aspect-ratio: 4/3)' +
    ' and (pointer: coarse)'
  const matchesFoldGeometry = (): boolean => {
    if (typeof window === 'undefined') return false
    const w = window.innerWidth
    const h = window.innerHeight
    // Inner display in landscape: wider than mobile, shorter than tablet,
    // both axes above the mobile fold-back thresholds.
    return w >= 700 && w <= 1000 && h >= 580 && h <= 900
  }
  const compute = (): boolean => {
    if (typeof window === 'undefined') return false
    if (window.matchMedia(QUERY).matches) return true
    return isGalaxyFoldUA() && matchesFoldGeometry()
  }
  const [isFold, setIsFold] = useState(compute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(QUERY)
    const handler = () => setIsFold(compute())
    mq.addEventListener('change', handler)
    window.addEventListener('resize', handler)
    return () => {
      mq.removeEventListener('change', handler)
      window.removeEventListener('resize', handler)
    }
  }, [])
  return isFold
}

/** Narrow-desktop detection — true when the viewport is too cramped
 *  to host a 280px fixed-width sidebar (Small laptop + Desktop monitor
 *  width tiers, see CLAUDE.md "Responsive breakpoints"). Pages that
 *  ship a synergies / info rail use this to fall back to a collapsible
 *  side tray instead, freeing the rail's horizontal real estate for
 *  the main content. Pairs with `useIsMobile()` — at viewports below
 *  720px, useIsMobile takes over and renders the mobile JSX path. */
export function useIsNarrowDesktop(): boolean {
  const QUERY = '(max-width: 1599px)'
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(QUERY).matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(QUERY)
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isNarrow
}

/** Portrait-only full-page overlay nudging the user to landscape.
 *
 *  Best-effort: Android Chrome honors `screen.orientation.lock` after
 *  we enter fullscreen via a user gesture; iOS Safari and desktop
 *  silently refuse. In refusal cases the prompt stays visible until
 *  the user rotates by hand. The CSS auto-hides the prompt under
 *  `@media (orientation: landscape)`. */
export function RotatePrompt(): ReactElement {
  const tryLockLandscape = async (): Promise<void> => {
    try {
      const el = document.documentElement as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void>
      }
      if (el.requestFullscreen) {
        await el.requestFullscreen()
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen()
      }
      const orientation = screen.orientation as ScreenOrientation & {
        lock?: (o: 'landscape' | 'portrait') => Promise<void>
      }
      if (typeof orientation?.lock === 'function') {
        await orientation.lock('landscape')
      }
    } catch {
      // iOS / desktop / refused-by-OS — silent. User rotates by hand.
    }
  }
  return (
    <div className="rotate-prompt" role="status" aria-live="polite">
      <div className="rotate-prompt__icon" aria-hidden="true">↻</div>
      <div className="rotate-prompt__heading">Rotate to landscape</div>
      <div className="rotate-prompt__sub">
        Turn your device sideways to continue.
      </div>
      <button
        type="button"
        className="rotate-prompt__cta"
        onClick={() => { void tryLockLandscape() }}
      >
        Try auto-rotate
      </button>
    </div>
  )
}
